import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  alpha,
  Grid,
  Stack,
  Switch,
  FormControlLabel,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import API, { uploadImage } from '../../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../../utils/imageUtils';
import useCategories from '../../hooks/useCategories';

const CATEGORY_COLOR_MAP = {
  Domestic: { color: '#3b82f6', bg: alpha('#3b82f6', 0.1) },
  International: { color: '#10b981', bg: alpha('#10b981', 0.1) },
  Religious: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1) },
  Wildlife: { color: '#22c55e', bg: alpha('#22c55e', 0.1) },
};

const getCategoryColor = (cat) =>
  CATEGORY_COLOR_MAP[cat] || CATEGORY_COLOR_MAP.Domestic;

const emptyForm = {
  image_url: '',
  imagePreview: null,
  description: '',
  category: 'Domestic',
  is_active: true,
};

const ManageOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dynamic categories
  const { categories: CATEGORIES, loading: catLoading } = useCategories();

  useEffect(() => { fetchOffers(); }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/offers');
      setOffers(data);
    } catch {
      setError('Failed to load exclusive offers.');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleOpenDialog = (offer = null) => {
    if (offer) {
      setEditingOffer(offer);
      setFormData({
        image_url: offer.image_url,
        imagePreview: getImageUrl(offer.image_url),
        description: offer.description || '',
        category: offer.category,
        is_active: !!offer.is_active,
      });
    } else {
      setEditingOffer(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOffer(null);
    setFormData(emptyForm);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({ ...prev, imagePreview: previewUrl }));

    try {
      setUploading(true);
      const result = await uploadImage(file, 'offer');
      setFormData(prev => ({ ...prev, image_url: result.filename || result.url || result.path }));
      showSnackbar('Image uploaded successfully!');
    } catch {
      showSnackbar('Image upload failed. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.image_url) {
      showSnackbar('Please upload an image.', 'error');
      return;
    }
    // Description validation removed - now optional

    try {
      setSaving(true);
      const payload = {
        image_url: formData.image_url,
        description: formData.description.trim(),
        category: formData.category,
        is_active: formData.is_active ? 1 : 0,
      };

      if (editingOffer) {
        await API.put(`/offers/${editingOffer.id}`, payload);
        showSnackbar('Offer updated successfully!');
      } else {
        await API.post('/offers', payload);
        showSnackbar('Offer created successfully!');
      }
      handleCloseDialog();
      fetchOffers();
    } catch {
      showSnackbar('Failed to save offer. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await API.delete(`/offers/${deleteId}`);
      showSnackbar('Offer deleted successfully!');
      setDeleteDialogOpen(false);
      setDeleteId(null);
      fetchOffers();
    } catch {
      showSnackbar('Failed to delete offer.', 'error');
    }
  };

  const handleToggleActive = async (offer) => {
    try {
      await API.put(`/offers/${offer.id}`, { is_active: offer.is_active ? 0 : 1 });
      fetchOffers();
    } catch {
      showSnackbar('Failed to update status.', 'error');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <LocalOfferIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Exclusive Holiday Offers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {offers.length} offer{offers.length !== 1 ? 's' : ''} total
            </Typography>
          </Box>
        </Box>
        <Button
          id="add-offer-btn"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            boxShadow: '0 4px 15px rgba(102,126,234,0.35)',
            '&:hover': { boxShadow: '0 6px 20px rgba(102,126,234,0.5)' },
          }}
        >
          Add New Offer
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Offers Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#667eea' }} />
        </Box>
      ) : offers.length === 0 ? (
        <Paper
          sx={{ p: 8, textAlign: 'center', borderRadius: 3, border: '2px dashed #e2e8f0' }}
        >
          <LocalOfferIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Exclusive Offers Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Click "Add New Offer" to create your first exclusive holiday offer.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
            sx={{ borderRadius: 2, textTransform: 'none', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Add New Offer
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {offers.map((offer) => {
            const cat = getCategoryColor(offer.category);
            return (
              <Grid item xs={12} sm={6} md={4} key={offer.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                    transition: 'all 0.3s ease',
                    opacity: offer.is_active ? 1 : 0.6,
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0,0,0,0.12)' },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(offer.image_url) || FALLBACK_IMAGE}
                      alt={offer.description || 'Offer image'}
                      onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
                      <Chip
                        label={offer.category}
                        size="small"
                        sx={{ bgcolor: cat.bg, color: cat.color, fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    </Box>
                    {!offer.is_active && (
                      <Box sx={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '12px 12px 0 0',
                      }}>
                        <Chip label="Inactive" sx={{ bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontWeight: 600 }} />
                      </Box>
                    )}
                  </Box>
                  {offer.description && (
                    <CardContent sx={{ pb: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.6,
                        }}
                      >
                        {offer.description}
                      </Typography>
                    </CardContent>
                  )}
                  <CardActions sx={{ px: 2, pb: 2, pt: offer.description ? 0 : 2, justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!offer.is_active}
                          onChange={() => handleToggleActive(offer)}
                          sx={{ '& .MuiSwitch-thumb': { bgcolor: offer.is_active ? '#667eea' : '#94a3b8' } }}
                        />
                      }
                      label={<Typography variant="caption" color="text.secondary">{offer.is_active ? 'Active' : 'Inactive'}</Typography>}
                    />
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton
                          id={`edit-offer-${offer.id}`}
                          size="small"
                          onClick={() => handleOpenDialog(offer)}
                          sx={{ color: '#667eea', bgcolor: alpha('#667eea', 0.08), '&:hover': { bgcolor: alpha('#667eea', 0.15) } }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          id={`delete-offer-${offer.id}`}
                          size="small"
                          onClick={() => { setDeleteId(offer.id); setDeleteDialogOpen(true); }}
                          sx={{ color: '#ef4444', bgcolor: alpha('#ef4444', 0.08), '&:hover': { bgcolor: alpha('#ef4444', 0.15) } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {editingOffer ? 'Edit Offer' : 'Add New Exclusive Offer'}
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            {/* Category */}
            <TextField
              id="offer-category"
              select
              label="Category / Tab"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              fullWidth
              size="small"
            >
              {catLoading ? <MenuItem value={formData.category}>{formData.category}</MenuItem> : CATEGORIES.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Offer Image *</Typography>
              {formData.imagePreview ? (
                <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 1 }}>
                  <Box
                    component="img"
                    src={formData.imagePreview}
                    alt="Preview"
                    sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                    onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setFormData(prev => ({ ...prev, imagePreview: null, image_url: '' }))}
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  {uploading && (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={32} sx={{ color: 'white' }} />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  component="label"
                  htmlFor="offer-image-upload"
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    border: '2px dashed #e2e8f0', borderRadius: 2, p: 4, cursor: 'pointer',
                    transition: 'all 0.2s', bgcolor: '#f8fafc',
                    '&:hover': { borderColor: '#667eea', bgcolor: alpha('#667eea', 0.04) },
                  }}
                >
                  {uploading ? (
                    <CircularProgress size={32} sx={{ color: '#667eea' }} />
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Click to upload image</Typography>
                      <Typography variant="caption" color="text.disabled">JPG, PNG, WEBP up to 10MB</Typography>
                    </>
                  )}
                  <input id="offer-image-upload" type="file" accept="image/*" hidden onChange={handleImageUpload} />
                </Box>
              )}
            </Box>

            {/* Description - Now Optional */}
            <TextField
              id="offer-description"
              label="Description (Optional)"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              placeholder="Describe this exclusive holiday offer..."
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.description.length}/500 characters`}
            />

            {/* Active toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: '#667eea' } }}
                />
              }
              label="Active (visible on homepage)"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            id="save-offer-btn"
            onClick={handleSave}
            variant="contained"
            disabled={saving || uploading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            {saving ? 'Saving...' : editingOffer ? 'Save Changes' : 'Add Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Offer?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove the offer. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            id="confirm-delete-offer-btn"
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageOffers;